/**
 * Site-wide form validation.
 *
 * Any <form novalidate> gets custom inline error messages instead of the
 * browser's native validation bubbles, driven by the same HTML5 constraints
 * already on the fields (required, type="email", pattern, min/minlength...).
 * No per-field JS wiring needed -- just add `novalidate` to the form tag.
 *
 * Optional per-field overrides: data-error-required="..." data-error-invalid="..."
 */
(function () {
  function defaultMessage(field) {
    var validity = field.validity;
    if (validity.valueMissing) {
      if (field.type === 'checkbox') return 'This field is required.';
      if (field.type === 'email') return 'Please enter your email address.';
      return field.dataset.errorRequired || 'This field is required.';
    }
    if (validity.typeMismatch || validity.patternMismatch) {
      if (field.type === 'email') return 'Please enter a valid email address.';
      return field.dataset.errorInvalid || field.validationMessage;
    }
    if (validity.tooShort) return 'Please use at least ' + field.minLength + ' characters.';
    if (validity.rangeUnderflow) return 'Please enter a value of at least ' + field.min + '.';
    if (validity.rangeOverflow) return 'Please enter a value of at most ' + field.max + '.';
    return field.validationMessage || 'Please check this field.';
  }

  function getMessage(field) {
    if (field.validity.valid) return null;
    return field.dataset.errorRequired && field.validity.valueMissing
      ? field.dataset.errorRequired
      : field.dataset.errorInvalid && !field.validity.valueMissing
      ? field.dataset.errorInvalid
      : defaultMessage(field);
  }

  function findExistingErrorEl(field) {
    // Card/checkbox-style choices (see quote-form-field.liquid's
    // "quote-chip" cards, and the plain checkbox-item lists on the
    // Wholesale/Apply-Wholesale forms): the error lives *inside* the
    // .field--checkbox wrapper, as its last child, so it stays part of that
    // one box's own layout instead of becoming a new item in the outer
    // field grid (which pushed later fields down - a plain input, own
    // width, own grid; a card, siblings, own grid width all conflict).
    var choiceWrap = field.closest('.field--checkbox');
    if (choiceWrap) {
      var last = choiceWrap.lastElementChild;
      return last && last.classList.contains('form-field-error') ? last : null;
    }
    // Phone fields get wrapped in a ".iti" container by intl-tel-input
    // (see phone-country-code.liquid) after this script first sees them, so
    // "field" is no longer a direct child of its original parent -
    // insertAdjacentElement('afterend', ...) on the bare input would land
    // *inside* that wrapper (as a new sibling of the input, still inside
    // .iti) instead of after it, inflating the wrapper's own height. Anchor
    // on the wrapper itself when present.
    var anchor = field.closest('.iti') || field;
    var next = anchor.nextElementSibling;
    return next && next.classList && next.classList.contains('form-field-error') ? next : null;
  }

  function ensureErrorEl(field) {
    var existing = findExistingErrorEl(field);
    if (existing) return existing;
    var errorEl = document.createElement('span');
    errorEl.className = 'form-field-error';
    errorEl.setAttribute('role', 'alert');
    errorEl.hidden = true;
    var choiceWrap = field.closest('.field--checkbox');
    if (choiceWrap) {
      choiceWrap.appendChild(errorEl);
    } else {
      (field.closest('.iti') || field).insertAdjacentElement('afterend', errorEl);
    }
    return errorEl;
  }

  function clearField(field) {
    field.classList.remove('form-field--invalid');
    field.removeAttribute('aria-invalid');
    var next = findExistingErrorEl(field);
    if (next && next.classList && next.classList.contains('form-field-error')) {
      next.hidden = true;
      next.textContent = '';
    }
  }

  function validateField(field) {
    if (!field.willValidate) return true;
    var message = getMessage(field);
    if (!message) {
      clearField(field);
      return true;
    }
    field.classList.add('form-field--invalid');
    field.setAttribute('aria-invalid', 'true');
    var errorEl = ensureErrorEl(field);
    errorEl.textContent = message;
    errorEl.hidden = false;
    return false;
  }

  function isManagedField(field) {
    return field.closest && !!field.closest('form[novalidate]');
  }

  document.addEventListener(
    'blur',
    function (event) {
      var field = event.target;
      if (field.matches && field.matches('input, textarea, select') && isManagedField(field)) {
        validateField(field);
      }
    },
    true
  );

  document.addEventListener(
    'input',
    function (event) {
      var field = event.target;
      if (
        field.matches &&
        field.matches('input, textarea') &&
        isManagedField(field) &&
        field.classList.contains('form-field--invalid')
      ) {
        validateField(field);
      }
    },
    true
  );

  document.addEventListener(
    'change',
    function (event) {
      var field = event.target;
      if (
        field.matches &&
        field.matches('select, input[type="checkbox"], input[type="radio"]') &&
        isManagedField(field)
      ) {
        validateField(field);
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form.matches || !form.matches('[novalidate]')) return;
      var fields = Array.prototype.slice.call(
        form.querySelectorAll('input, textarea, select')
      );
      var results = fields.map(validateField);
      var firstInvalidIndex = results.indexOf(false);
      if (firstInvalidIndex !== -1) {
        event.preventDefault();
        event.stopImmediatePropagation();
        fields[firstInvalidIndex].focus();
      }
    },
    true
  );

  // Exposed so other scripts that gate their own multi-step flows (e.g. the
  // hero quote-form wizard's per-panel "Continue" button, in
  // quote-form-ajax.js) can reuse this exact same custom-error-message
  // behavior instead of falling back to the browser's native validation
  // bubble, which doesn't match the rest of the site.
  window.themeFormValidation = { validateField: validateField };
})();
